import { AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

interface LoadFailureProps {
  /** Retries the failed fetch. Omit when there is nothing useful to retry. */
  onRetry?: () => void;
  titleKey?: string;
  bodyKey?: string;
}

/**
 * Shown when a fetch FAILED, in place of an empty state.
 *
 * The distinction matters: telling a student with 40 completed exams that they have
 * none — and inviting them to start their first — is worse than showing an error,
 * because it looks like their data was destroyed rather than briefly unavailable.
 */
export const LoadFailure = ({
  onRetry,
  titleKey = "errors.could_not_load_title",
  bodyKey = "errors.could_not_load_body",
}: LoadFailureProps) => {
  const { t } = useTranslation();

  return (
    <div
      role="alert"
      className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-24 text-center"
    >
      <AlertCircle className="size-8 text-[#EB7B73]" aria-hidden="true" />
      <p className="text-black text-lg font-semibold font-inter">{t(titleKey)}</p>
      <p className="max-w-sm text-sm font-normal font-inter text-[#5B5F67]">
        {t(bodyKey)}
      </p>
      {onRetry && (
        <Button variant="outline" className="mt-2" onClick={onRetry}>
          {t("common.retry")}
        </Button>
      )}
    </div>
  );
};

export default LoadFailure;
